import React, { FC, useMemo } from "react";
import { BurnQualityCountAggregateByTime } from "../BurnQualityCountAggregateByTime";
import { SxProps, useTheme } from "@mui/material";
import {
  AxisData,
  MuiStructuralChartContainer,
  TimeBandAxisZoomOptions,
  useInitialTimeBandZoom,
  ZoomableTimeBandAxis,
} from "shared-ts-mui";
import {
  BarPlot,
  ChartDataProviderPro,
  ChartsAxisHighlight,
  ChartsClipPath,
  ChartsLegend,
  ChartsReferenceLine,
  ChartsSurface,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  ChartZoomSlider,
  LinePlot,
  MarkElement,
  MarkElementProps,
  MarkPlot,
  useChartProApiRef,
} from "@mui/x-charts-premium";
import { DateTime, DateTimeUnit, Interval } from "luxon";
import { useTranslation } from "react-i18next";
import { MathUtils } from "shared-ts-utils-core";
import { SizeClass } from "shared-ts-ui-responsive";
import { useDataSet } from "./useDataSet";
import { useResolveDisplayInterval } from "./useResolveDisplayInterval";
import { useYAxis } from "./useYAxis";
import { useSeries } from "./useSeries";
import { useResolveAverageGoodInPercent } from "./useResolveAverageGoodInPercent";
import { useXAxis } from "./useXAxis";
import { useResizeDetector } from "react-resize-detector";
import { merge } from "lodash";
import { ConditionalWrap } from "shared-ts-ui-react";

const defaultZoomOptions: Partial<TimeBandAxisZoomOptions> = {
  preferredTicks: 12,
  minTickSize: 75,
  maxTickSize: 150,
};
export interface BurnQualityCountAggregateByTimeChartProps {
  interval: Interval<true>;
  timeUnit: DateTimeUnit;
  thresholdFactor?: number;
  aggregates?: Array<BurnQualityCountAggregateByTime>;
  showBurnQualityCount?: boolean;
  showGoodInPercent: boolean;
  showRawGoodInPercent?: boolean;
  loading?: boolean;
  hideAverageGoodInPercent?: boolean;
  hideYAxisLabels?: boolean;
  xAxisZoomOptions?: boolean | Partial<TimeBandAxisZoomOptions>;
  layout?: SizeClass;
  sx?: SxProps;
  /**
   * Fires when a bar item in the chart is clicked. Provides the month (1-12) and year
   * that correspond to the clicked bar's time band.
   */
  onBarItemClick?: (e: { month: number; year: number }) => void;
}

export const BurnQualityCountAggregateByTimeChart: FC<
  BurnQualityCountAggregateByTimeChartProps
> = (props) => {
  const { interval, timeUnit, aggregates } = props;
  const loading = props.loading ?? false;
  const layout = props.layout ?? SizeClass.ExtraLarge;
  const hideYAxisLabels = props.hideYAxisLabels ?? layout === SizeClass.Compact;
  const hideAverageGoodInPercent = props.hideAverageGoodInPercent ?? false;
  const showIgnitionQualityCount = props.showBurnQualityCount ?? true;
  const showGoodInPercent = props.showGoodInPercent;
  const showRawGoodInPercent = props.showRawGoodInPercent ?? false;
  const clipPathId = React.useId();
  const theme = useTheme();
  const { t } = useTranslation("firefly-shared-ts-domain");
  const { ref, width } = useResizeDetector({
    handleHeight: false,
  });
  const svgRef = React.useRef<SVGSVGElement>(null);
  const apiRef = useChartProApiRef<"line" | "bar">();
  const zoomEnabled =
    props.xAxisZoomOptions === true ||
    (props.xAxisZoomOptions !== undefined &&
      props.xAxisZoomOptions !== false);
  const zoomOptions = useMemo(
    () =>
      zoomEnabled
        ? merge({}, defaultZoomOptions, props.xAxisZoomOptions)
        : undefined,
    [props.xAxisZoomOptions, zoomEnabled]
  );
  const zoomableTimeAxis: ZoomableTimeBandAxis = useMemo(() => {
    return {
      min: interval.start,
      max: interval.end,
      zoom: zoomOptions,
    };
  }, [interval.end, interval.start, zoomOptions]);
  const xAxis = useXAxis(zoomableTimeAxis, timeUnit, width);

  const displayInterval = useResolveDisplayInterval(interval, timeUnit);
  const dataSet = useDataSet(aggregates, displayInterval, timeUnit);

  const averageGoodInPercent = useResolveAverageGoodInPercent(dataSet);
  const yAxis = useYAxis(showIgnitionQualityCount, hideYAxisLabels, t);
  const series = useSeries(
    showIgnitionQualityCount,
    showGoodInPercent,
    showRawGoodInPercent,
    t
  );

  useInitialTimeBandZoom({
    apiRef,
    zoomableAxis: zoomableTimeAxis,
    timeUnit,
    width,
    zoomEnabled,
  });

  // Bubble bar click events with month/year information
  const handleAxisClick = (event: MouseEvent, axisData: AxisData | null) => {
    if (
      props.onBarItemClick &&
      axisData != null &&
      axisData?.axisValue &&
      (axisData?.axisValue as unknown) instanceof DateTime
    ) {
      const dt = axisData.axisValue as unknown as DateTime;
      props.onBarItemClick({ month: dt.month, year: dt.year });
    }
  };

  return (
    <MuiStructuralChartContainer ref={ref} loading={loading} sx={props.sx}>
      <ChartDataProviderPro
        apiRef={apiRef}
        xAxis={xAxis}
        yAxis={yAxis}
        series={series}
        dataset={dataSet as any}
        margin={{
          top: 10,
          left: hideYAxisLabels ? 10 : 10,
          right: hideYAxisLabels ? 40 : 70,
          bottom: 0,
        }}
        onAxisClick={handleAxisClick}
      >
        <ChartsSurface
          ref={svgRef}
          sx={{
            ".MuiMarkElement-root": {
              scale: "1.2",
            },
            ".MuiLineElement-series-goodInPercent": {
              strokeWidth: 5,
              filter: "drop-shadow(2px 3px 3px rgba(0, 0, 0, 0.9))",
            },
          }}
        >
          <ConditionalWrap
            condition={zoomEnabled}
            wrap={(children) => (
              <g clipPath={`url(#${clipPathId})`}>{children}</g>
            )}
          >
            <BarPlot borderRadius={6} barLabel={"value"} />
            <LinePlot />
            <MarkPlot
              slots={{
                mark: (props: MarkElementProps) => {
                  return <MarkElement {...props} />;
                },
              }}
            />
            <ChartsAxisHighlight
              x={showIgnitionQualityCount ? "band" : "line"}
            />
          </ConditionalWrap>
          <ChartsXAxis position="bottom" axisId="timeBand" />
          {showIgnitionQualityCount && (
            <ChartsYAxis position="left" axisId="burnCount" />
          )}
          {showGoodInPercent && (
            <ChartsYAxis
              label={hideYAxisLabels ? undefined : t("Percentage")}
              position="right"
              axisId="percentage"
            />
          )}
          {zoomEnabled && <ChartZoomSlider />}
          {zoomEnabled && <ChartsClipPath id={clipPathId} />}
          {showGoodInPercent &&
            !showIgnitionQualityCount &&
            !hideAverageGoodInPercent && (
              <ChartsReferenceLine
                y={averageGoodInPercent}
                label={`${t("Average")}: ${MathUtils.round(
                  averageGoodInPercent
                )} %`}
                labelAlign={"start"}
                labelStyle={{
                  fontSize: "smaller",
                  fill: theme.palette.text.secondary,
                }}
                lineStyle={{
                  strokeDasharray: "10 5",
                  stroke: theme.palette.text.secondary,
                }}
                axisId={"percentage"}
              />
            )}

          <ChartsTooltip trigger={"axis"} />
        </ChartsSurface>
        {showIgnitionQualityCount && (
          <ChartsLegend sx={{ justifyContent: "center" }} />
        )}
      </ChartDataProviderPro>
    </MuiStructuralChartContainer>
  );
};
